import { useState, useEffect, useCallback, type ReactNode } from 'react';

import { router } from '@/app/router';
import { GET_CURRENT_USER } from '@modules/auth';
import { urqlClient } from '@shared/api';
import {
  refetchToken,
  logout as logoutUtil,
  isOnPublicAuthPath,
  redirectToLogin,
  safeRedirectPath,
  type RefreshedSession,
} from '@shared/auth/auth';
import { USER_STORAGE_KEY, userStorage } from '@shared/auth/auth-route';
import { AuthError, AUTH_ERROR_CODE } from '@shared/auth/errors';
import { clearImpersonation, confirmImpersonation } from '@shared/auth/impersonation';
import { tokenStore, clearLoggedOutFlag, isImpersonating } from '@shared/auth/token.store';
import { AuthContext, type AuthContextValue } from '@shared/auth/use-auth';
import { useTokenRefresh } from '@shared/auth/use-token-refresh';
import {
  collectWorkspaces,
  WORKSPACES_QUERY,
  type WorkspacesData,
} from '@shared/hooks/use-workspaces';
import { themeFromApi } from '@shared/lib/theme';
import { featureFlagsStore } from '@shared/stores/feature-flags.store';
import { useUIStore } from '@shared/stores/ui.store';
import type { User } from '@shared/types/auth';

function saveUser(user: User): void {
  userStorage().setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function loadUser(): User | null {
  try {
    const raw = userStorage().getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function removeUser(): void {
  userStorage().removeItem(USER_STORAGE_KEY);
}

/** Normalize GQL `[{ key, enabled }]` array into `Record<string, boolean>`. */
function normalizeFeatureFlags(
  raw: Array<{ key: string; enabled: boolean }> | Record<string, boolean> | undefined,
): Record<string, boolean> {
  if (!raw) return {};
  if (Array.isArray(raw)) {
    return Object.fromEntries(raw.map((f) => [f.key, f.enabled]));
  }
  return raw;
}

function isAccessDenied(error: { graphQLErrors: Array<{ extensions?: unknown }> } | undefined) {
  return (
    error?.graphQLErrors.some((e) => {
      const code = (e.extensions as Record<string, unknown> | undefined)?.['code'];
      return code === 'UNAUTHENTICATED' || code === 'FORBIDDEN';
    }) ?? false
  );
}

function toUser(raw: Record<string, unknown>): User {
  return { ...raw, featureFlags: normalizeFeatureFlags(raw.featureFlags as never) } as User;
}

function applyUserTheme(user: User): void {
  const theme = themeFromApi(user.theme);
  if (theme) useUIStore.getState().setTheme(theme);
}

function syncFeatureFlags(user: User): void {
  // The API has no `featureFlags` on User, so this normalises to {} today. The
  // call is kept so the store has a single writer once a flag source is decided.
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- see above
  featureFlagsStore.getState().setFlags(user.featureFlags);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(loadUser);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const adoptRefreshedUser = useCallback((session: RefreshedSession | undefined) => {
    const refreshed = session?.user;
    if (!refreshed || !('avatar' in refreshed)) return;
    setUser((prev) => {
      if (!prev || prev.avatar === (refreshed.avatar ?? undefined)) return prev;
      const next = { ...prev, avatar: refreshed.avatar ?? undefined };
      saveUser(next);
      return next;
    });
  }, []);

  // Proactive token refresh every 60s
  useTokenRefresh(adoptRefreshedUser);

  // Silent refresh on mount — always attempt, even on page refresh when the
  // in-memory token is gone. appRoute.beforeLoad passed only if a user exists
  // in localStorage; now we validate the session against the server via the
  // httpOnly refresh-token cookie. AppShell shows PageSkeleton while isLoading.
  useEffect(() => {
    const attemptSilentRefresh = async () => {
      let redirecting = false;
      try {
        const session = await refetchToken();

        // Restore user from localStorage, or fetch from server if not stored
        // (happens when the login response didn't include a user object).
        let currentUser = loadUser();
        if (!currentUser) {
          const result = await urqlClient.query(GET_CURRENT_USER, {}).toPromise();
          if (isImpersonating() && isAccessDenied(result.error)) {
            redirecting = true;
            redirectToLogin(true);
            return;
          }
          const meData = result.data as { me?: Record<string, unknown> } | null;
          if (meData?.me) {
            const meUser = toUser(meData.me);
            saveUser(meUser);
            currentUser = meUser;
          }
        }

        if (currentUser) {
          if (isImpersonating()) confirmImpersonation();
          syncFeatureFlags(currentUser);
          applyUserTheme(currentUser);
        }
        setUser((prev) => prev ?? currentUser);
        adoptRefreshedUser(session);
      } catch (err) {
        // Only treat definitive authentication rejections (401 / 403) as
        // "session expired". Network outages or 5xx responses should not
        // silently log the user out — they can retry when the server is back.
        const isAuthError =
          err instanceof AuthError && err.code === AUTH_ERROR_CODE.UNAUTHENTICATED;

        if (isAuthError) {
          removeUser();
          setUser(null);
          tokenStore.getState().clearAccessToken();
          // Don't bounce off public auth pages (recovery/reset have no session).
          if (!isOnPublicAuthPath()) {
            redirecting = true;
            void router.navigate({ to: '/login' });
          }
        }
      } finally {
        if (!redirecting) setIsLoading(false);
      }
    };

    void attemptSilentRefresh();
  }, [adoptRefreshedUser]);

  // ── REST login ────────────────────────────────────────────────────────────

  const login = useCallback(async (username: string, password: string, remember = true) => {
    // DEV-3366: base64-encode the credentials as UTF-8, not Latin-1. `btoa`
    // is Latin-1-only — non-ASCII chars either throw (CJK, Cyrillic) or map
    // to the wrong byte (0x80–0xFF), so a password like "café" or "Пароль"
    // sends a different byte sequence than the old CRM (`Buffer.from(...)`
    // defaults to UTF-8) even though both hit the same backend + bcrypt
    // hash, and login silently 403s after a password change.
    const bytes = new TextEncoder().encode(`${username}:${password}`);
    const credentials = btoa(String.fromCharCode(...bytes));
    const res = await fetch(`/api/login?remember=${remember ? '1' : '0'}`, {
      credentials: 'include',
      headers: {
        authorization: `Basic ${credentials}`,
      },
    });

    if (res.status === 403) throw new AuthError(AUTH_ERROR_CODE.INVALID_CREDENTIALS);
    if (!res.ok) throw new AuthError(AUTH_ERROR_CODE.SERVER_UNAVAILABLE);

    const data = (await res.json()) as {
      token: string;
      accessTokenTTL?: number;
      user?: Record<string, unknown>;
    };

    if (!data.token) throw new AuthError(AUTH_ERROR_CODE.TOKEN_NOT_RECEIVED);

    // A fresh, explicit login supersedes any prior logout — let silent
    // refreshers (router bootstrap, 60s poll, urql/ws exchanges) resume.
    clearImpersonation();
    clearLoggedOutFlag();
    tokenStore.getState().setAccessToken(data.token, data.accessTokenTTL);

    let authUser = data.user ? toUser(data.user) : null;
    if (!authUser) {
      const result = await urqlClient.query(GET_CURRENT_USER, {}).toPromise();
      const meData = result.data as { me?: Record<string, unknown> } | null;
      if (meData?.me) authUser = toUser(meData.me);
    }
    if (authUser) {
      saveUser(authUser);
      setUser(authUser);
      syncFeatureFlags(authUser);
      applyUserTheme(authUser);
    }

    const urlParam = new URLSearchParams(window.location.search).get('url');
    // Validate against open redirect — only same-origin relative paths.
    const destination = safeRedirectPath(urlParam);

    // DEV-4200: gate on the company switcher when there's more than one to
    // choose from. Best-effort — a failed/errored workspaces query must never
    // block sign-in, so any issue here just falls through to the normal
    // destination.
    let workspaceCount = 1;
    try {
      const result = await urqlClient.query<WorkspacesData>(WORKSPACES_QUERY, {}).toPromise();
      workspaceCount = collectWorkspaces(result.data).workspaces.length;
    } catch {
      // ignore — treated as "nothing to choose from"
    }

    if (workspaceCount > 1) {
      void router.navigate({ to: '/choose-company', search: { url: destination } });
      return;
    }

    void router.navigate({ to: destination });
  }, []);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    const impersonating = isImpersonating();
    await logoutUtil();
    if (!impersonating) removeUser();
    if (isOnPublicAuthPath()) setIsLoggingOut(false);
  }, []);

  const updateUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      saveUser(next);
      return next;
    });
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isLoggingOut,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
